import React, { useState, useRef, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import RouteForm from './components/RouteForm';
import ResultsPanel from './components/ResultsPanel';
import Login from './components/Login';
import Signup from './components/Signup';
import SavingsDashboard from './components/SavingsDashboard';
import './tokens.css';
import './App.css';
import TeamPage from "./components/TeamPage";
import axios from 'axios';

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            localStorage.removeItem('tankly_token');
            sessionStorage.removeItem('tankly_form_data');
            sessionStorage.removeItem('tankly_results');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

function Dashboard() {
    const [results, setResults] = useState(() => {
        const saved = sessionStorage.getItem('tankly_results');
        return saved ? JSON.parse(saved) : null;
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const resultsRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('tankly_token');
        if (!token) {
            navigate('/login');
            return;
        }
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            // payload.exp is in seconds, Date.now() is in milliseconds
            if (payload.exp * 1000 < Date.now()) {
                localStorage.removeItem('tankly_token');
                sessionStorage.removeItem('tankly_form_data');
                sessionStorage.removeItem('tankly_results');
                navigate('/login');
            }
        } catch (e) {
            navigate('/login');
        }
    }, [navigate]);

    const handleResults = (data) => {
        setResults(data);
        sessionStorage.setItem('tankly_results', JSON.stringify(data));
        setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    };

    const handleLogout = () => {
        localStorage.removeItem('tankly_token');
        sessionStorage.removeItem('tankly_form_data');
        sessionStorage.removeItem('tankly_results');
        navigate('/login');
    };

    const token = localStorage.getItem('tankly_token');
    let isAdmin = false;
    if (token) {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            isAdmin = payload.role === 'ROLE_COMPANY_ADMIN';
        } catch (e) {
            console.error("Error decoding token.", e);
        }
    }

    const isMaintenanceMode = false;
    if (isMaintenanceMode) {
        return (
            <div className="maintenance-screen">
                <h1>Work in progress</h1>
                <p>We're improving Tankly's security. Back soon.</p>
                <p className="muted">Thank you for your patience — The Tankly Team</p>
            </div>
        );
    }

    return (
        <div className="app">
            <a className="skip-link" href="#main-content">Skip to main content</a>

            <header className="app-header">
                <img className="app-logo" src="/tankly_logo.svg" alt="Tankly" />
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    {isAdmin && (
                        <>
                            <a href="/team" style={{
                                padding: '8px 16px',
                                border: '1.5px solid #DADFE5',
                                borderRadius: '8px',
                                color: '#1C3A5E',
                                fontWeight: 600,
                                fontSize: '14px',
                                textDecoration: 'none',
                                background: 'white'
                            }}>View My Team</a>
                            <a href="/savings" style={{
                                padding: '8px 16px',
                                border: '1.5px solid #DADFE5',
                                borderRadius: '8px',
                                color: '#1C3A5E',
                                fontWeight: 600,
                                fontSize: '14px',
                                textDecoration: 'none',
                                background: 'white'
                            }}>View Savings</a>
                        </>
                    )}
                    <button onClick={handleLogout} className="btn-logout" aria-label="Log out of Tankly">
                        Log out
                    </button>
                </div>
            </header>

            <main id="main-content">
                <h1 className="app-title">Find your best fuel stop</h1>
                <RouteForm setResults={handleResults} setLoading={setLoading} setError={setError} />

                <div aria-live="polite">
                    {loading && (
                        <div className="loading-state">
                            <div className="spinner" aria-hidden="true"></div>
                            <p>Finding the best stations along your route…</p>
                        </div>
                    )}
                    {error && (
                        <div className="alert alert-error" role="alert">
                            {error}
                        </div>
                    )}
                </div>

                {results && !loading && (
                    <div ref={resultsRef}>
                        <ResultsPanel results={results} />
                    </div>
                )}
            </main>
            <Analytics />
        </div>
    );
}

function App() {
    return (
        <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/savings" element={<SavingsDashboard />} />
            <Route path="/team" element={<TeamPage  />} />
        </Routes>
    );
}

export default App;
